import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UserActionType } from '@store/branches/user/enums';
import { useDispatch } from 'react-redux';
import authContext from '@contexts/AuthContext';
import GlobalNavbar from '@components/GlobalNavbar';
import NavSidebar from '@components/NavSidebar';
import AppRoutes from '@utilities/app-routes';
import { menuItemRenderer } from '@components/NavSidebar/utils';
import { matchRoutes, useLocation, useNavigate } from 'react-router';
import { PERMISSIONS, toast } from '@utilities';
import NotAuthorized from '@components/NotAuthorized';
import ErrorBoundary from '@components/ErrorBoundary';
import UpdateBanner from '@components/UpdateBanner';
import { ROUTES } from '@src/app';
import { useAppSelector } from '@store/selectors';
import { ProgressSpinner } from 'primereact/progressspinner';
import { fetchCurrentUserPermissions } from '@utilities/commonActions';
import { MdOutlineEmail } from 'react-icons/md';
import { TbSitemap } from 'react-icons/tb';
import { isEmailConfigAdmin } from '@containers/emailConfig/utils';
import {
	Chart,
	Profile2User,
	BoxTick,
	Lamp,
	WalletMoney,
	Box,
	Category,
	ShieldTick,
	Gallery,
	TaskSquare,
	Clock,
	DocumentText,
	MinusCirlce,
	Ticket,
	Calculator,
	DiscountShape,
	Cup,
	MessageText,
	Calendar
} from 'iconsax-react';

export const RootLayout: React.FunctionComponent<any> = ({ permission, children }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
	const [showSidebar, setShowSidebar] = useState(() => {
		if (isMobile) return false;
		const saved = localStorage.getItem('sidebar-visible');
		return saved !== null ? JSON.parse(saved) : true;
	});
	const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
		const saved = localStorage.getItem('sidebar-collapsed');
		return saved !== null ? JSON.parse(saved) : false;
	});
	const { pathname } = useLocation();
	const { isUserLoading, hasPermission, user, setUser, logout } = useContext(authContext) as any;
	const matchedRoutes = useMemo(() => matchRoutes(ROUTES, pathname)?.map(r => r.route.path) || [], [pathname]);

	const [hasModulePermission, setHasModulePermission] = useState(true);
	const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);
	const [filteredMenuItems, setFilteredMenuItems] = useState<any[]>([]);
	const hasAllPermissions = (requiredPermissions?: string | string[]) => {
		if (!requiredPermissions) return true;
		const permissionList = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
		return permissionList.every(requiredPermission => hasPermission(requiredPermission));
	};

	useEffect(() => {
		localStorage.setItem('sidebar-visible', JSON.stringify(showSidebar));
	}, [showSidebar]);

	useEffect(() => {
		localStorage.setItem('sidebar-collapsed', JSON.stringify(sidebarCollapsed));
	}, [sidebarCollapsed]);

	useEffect(() => {
		const updatedItems = MENU_ITEMS.map(item => ({
			...item,
			activeKeys: matchedRoutes
		}));
		setFilteredMenuItems(updatedItems);
	}, [location.pathname]);

	const { userPermissionsList: permissions } = useAppSelector(state => state.user);

	useEffect(() => {
		if (!isPermissionsLoading) {
			setHasModulePermission(!permission || hasAllPermissions(permission));
			const values = filterMenuItems(MENU_ITEMS);
			setFilteredMenuItems(values);
		}
	}, [permissions, permission, hasPermission, location.pathname, user?.department, user?.designation, isPermissionsLoading]);

	useEffect(() => {
		if (user?.userId === undefined) {
			dispatch({
				type: UserActionType.FETCH_USER_DETAILS,
				payload: {
					successCB: userRes => {
						setUser(userRes);
					},
					errorCB: message => {
						toast('ERROR', message);
						setIsPermissionsLoading(false);
					}
				}
			});
		}

		if (user?.userId) {
			fetchCurrentUserPermissions(dispatch, user.userId, message => {
				setIsPermissionsLoading(false);
				toast('ERROR', message);
			}, () => setIsPermissionsLoading(false));
		}
	}, [user?.userId, dispatch, setUser]);

	const MENU_ITEMS = [
		{
			key: 'dashboard',
			label: 'Dashboard',
			route: AppRoutes.DASHBOARD,
			template: menuItemRenderer,
			icon: <Chart size={16} variant="Outline" color="currentColor" />,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.DASHBOARD);
			}
		},
		{
			key: 'leads',
			label: 'Leads',
			route: AppRoutes.LEADS,
			icon: <Profile2User size={16} variant="Outline" color="currentColor" />,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.LEADS);
			},
			permission: PERMISSIONS.LEAD_READ
		},
		{
			key: 'projects',
			label: 'Projects',
			route: AppRoutes.PROJECTS,
			template: menuItemRenderer,
			icon: <Lamp size={16} variant="Outline" color="currentColor" />,
			activeKeys: matchedRoutes,
			command: () => navigate(AppRoutes.ALL_PROJECTS),
			permission: PERMISSIONS.PROJECT_READ
		},
		{
			key: 'orders',
			label: 'Orders',
			route: AppRoutes.ORDERS,
			template: menuItemRenderer,
			icon: <BoxTick size={16} variant="Outline" color="currentColor" />,
			activeKeys: matchedRoutes,
			command: () => navigate(AppRoutes.ALL_ORDERS),
			permission: PERMISSIONS.PROJECT_READ
		},
		{
			key: 'products',
			label: 'Products',
			icon: <Box size={16} variant="Outline" color="currentColor" />,
			route: AppRoutes.PRODUCTS,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			items: [
				{
					key: 'all-categories',
					label: 'All Categories',
					route: AppRoutes.ALL_CATEGORIES,
					icon: <Category size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.ALL_CATEGORIES);
					},
					permission: PERMISSIONS.CATEGORIES_READ
				},
				{
					key: 'variants-grouping',
					label: 'Variants Grouping',
					route: AppRoutes.VARIANTS_GROUPING,
					icon: <TbSitemap />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.VARIANTS_GROUPING);
					},
					permission: PERMISSIONS.CATEGORIES_READ
				},
				{
					key: 'parametric-variants',
					label: 'Parametric Variants',
					route: AppRoutes.PARAMETRIC_VARIANTS,
					icon: <TbSitemap />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => navigate(AppRoutes.PARAMETRIC_VARIANTS),
					permission: PERMISSIONS.CATEGORIES_READ
				},
				{
					key: 'all-products',
					label: 'All Products',
					route: AppRoutes.ALL_PRODUCTS,
					icon: <Box size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.ALL_PRODUCTS);
					},
					permission: PERMISSIONS.PRODUCT_READ
				},
				{
					key: 'all-warranty',
					label: 'All Warranty',
					route: AppRoutes.ALL_WARRANTY,
					icon: <ShieldTick size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.ALL_WARRANTY);
					},
					permission: PERMISSIONS.WARRANTY_READ
				},
				{
					key: 'default-models',
					label: 'Default 3D Models',
					route: AppRoutes.DEFAULT_MODELS,
					icon: <Box size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.DEFAULT_MODELS);
					},
					permission: PERMISSIONS.ADMIN
				}
			]
		},
		{
			key: 'inspirations',
			label: 'Inspirations',
			icon: <Gallery size={16} variant="Outline" color="currentColor" />,
			route: AppRoutes.INSPIRATIONS,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			items: [
				{
					key: 'all-inspirations',
					label: 'All Inspirations',
					route: AppRoutes.ALL_INSPIRATIONS,
					icon: <Gallery size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.ALL_INSPIRATIONS);
					},
					permission: PERMISSIONS.INSPIRATION_READ
				}
			]
		},
		{
			key: 'commercials',
			label: 'Commercials',
			icon: <WalletMoney size={16} variant="Outline" color="currentColor" />,
			route: AppRoutes.COMMERCIALS,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			items: [
				{
					key: 'payments',
					label: 'Payments',
					route: AppRoutes.COMMERCIALS_PAYMENT,
					icon: <WalletMoney size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.COMMERCIALS_PAYMENT);
					},
					permission: PERMISSIONS.COMMERCIAL_READ
				}
			]
		},
		{
			key: 'approvals',
			label: 'Approvals',
			icon: <TaskSquare size={16} variant="Outline" color="currentColor" />,
			route: AppRoutes.APPROVAL,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			items: [
				{
					key: 'approval-delay',
					label: 'Delay',
					route: AppRoutes.APPROVAL_DELAY,
					icon: <Clock size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.APPROVAL_DELAY);
					},
					permission: PERMISSIONS.APPROVAL_READ
				},
				{
					key: 'approval-document',
					label: 'Document',
					route: AppRoutes.APPROVAL_DOCUMENT,
					icon: <DocumentText size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.APPROVAL_DOCUMENT);
					},
					permission: PERMISSIONS.APPROVAL_READ
				},
				{
					key: 'approval-warranty',
					label: 'Warranty',
					route: AppRoutes.APPROVAL_WARRANTY,
					icon: <ShieldTick size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.APPROVAL_WARRANTY);
					},
					permission: PERMISSIONS.APPROVAL_READ
				},
				{
					key: 'approval-descope',
					label: 'Descope',
					route: AppRoutes.APPROVAL_DESCOPE,
					icon: <MinusCirlce size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.APPROVAL_DESCOPE);
					},
					permission: PERMISSIONS.APPROVAL_READ
				},
				{
					key: 'approval-product-item',
					label: 'Product',
					route: AppRoutes.APPROVAL_PRODUCT_ITEM,
					icon: <BoxTick size={16} variant="Outline" color="currentColor" />,
					template: menuItemRenderer,
					activeKeys: matchedRoutes,
					command: () => {
						navigate(AppRoutes.APPROVAL_PRODUCT_ITEM);
					},
					permission: PERMISSIONS.APPROVAL_READ
				}
			]
		},
		{
			key: 'tickets',
			label: 'Tickets',
			route: AppRoutes.ALL_TICKETS,
			template: menuItemRenderer,
			icon: <Ticket size={16} variant="Outline" color="currentColor" />,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.ALL_TICKETS);
			},
			permission: PERMISSIONS.TICKET_READ
		},
		{
			key: 'costing',
			label: 'Builder Costing',
			route: AppRoutes.BUILDER_COSTING,
			template: menuItemRenderer,
			icon: <Calculator size={16} variant="Outline" color="currentColor" />,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.BUILDER_COSTING);
			},
			permission: PERMISSIONS.COSTING_READ
		},
		{
			key: 'promo-code',
			label: 'Promo Code',
			route: AppRoutes.PROMO_CODE,
			icon: <DiscountShape size={16} variant="Outline" color="currentColor" />,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.PROMO_CODE);
			},
			permission: PERMISSIONS.PROMO_CODE_READ
		},
		{
			key: 'incentives',
			label: 'Incentives',
			route: AppRoutes.INCENTIVES,
			icon: <Cup size={16} variant="Outline" color="currentColor" />,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.INCENTIVES);
			},
			permission: PERMISSIONS.ADMIN
		},
		{
			key: 'feedback',
			label: 'Feedback',
			route: AppRoutes.FEEDBACK,
			icon: <MessageText size={16} variant="Outline" color="currentColor" />,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.FEEDBACK);
			}
		},
		...(isEmailConfigAdmin(user)
			? [
					{
						key: 'email-config',
						label: 'Email Config',
						route: AppRoutes.EMAIL_CONFIG,
						icon: <MdOutlineEmail />,
						template: menuItemRenderer,
						activeKeys: matchedRoutes,
						command: () => {
							navigate(AppRoutes.EMAIL_CONFIG);
						}
					}
				]
			: []),
		{
			key: 'holidays',
			label: 'Holiday Calendar',
			route: AppRoutes.HOLIDAYS,
			icon: <Calendar size={16} variant="Outline" color="currentColor" />,
			template: menuItemRenderer,
			activeKeys: matchedRoutes,
			command: () => {
				navigate(AppRoutes.HOLIDAYS);
			}
		}
	];

	const hasThePermission = (requiredPermissions?: string | string[]) => {
		if (!requiredPermissions) return true;
		const permissionList = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];
		return permissionList.every(requiredPermission => {
			const permissionObj = permissions.find(p => p.permission === requiredPermission);
			return !!permissionObj?.isAssigned;
		});
	};

	const filterMenuItems = (items: any) => {
		const filteredItems: any = [];
		items.forEach((element: any) => {
			const next = { ...element };
			if (next.items?.length) {
				next.items = filterMenuItems(next.items);
			}

			const selfAllowed = !next.permission || hasThePermission(next.permission);
			if (!selfAllowed) {
				// Parent denied: still show if any allowed children remain.
				if (next.items?.length) filteredItems.push(next);
				return;
			}

			if (next.items) {
				if (next.items.length > 0) filteredItems.push(next);
				return;
			}

			filteredItems.push(next);
		});
		return filteredItems;
	};

	return (
		<>
			{isPermissionsLoading ? (
				<div className="flex items-center justify-center w-screen h-screen bg-warm-bg">
					<ProgressSpinner />
				</div>
			) : hasModulePermission ? (
				<div className="flex antialiased grow bg-warm-bg">
					{showSidebar && (
						<NavSidebar
							show={showSidebar}
							menuList={filteredMenuItems}
							collapsed={sidebarCollapsed}
							onToggleCollapse={() => setSidebarCollapsed(prev => !prev)}
							onClose={() => {
								setShowSidebar(false);
							}}
						/>
					)}
					<div className="w-full min-w-0">
						<GlobalNavbar
							logout={logout}
							isUserLoading={isUserLoading}
							user={user || {}}
							matchedRoutes={matchedRoutes}
							sidebarVisible={showSidebar}
							onMenuToggle={() => setShowSidebar(prev => !prev)}
						/>
						<div className="h-[calc(100vh-3.5rem)] p-4 md:p-6 overflow-y-auto bg-[var(--color-bg)]">
							<ErrorBoundary>{children}</ErrorBoundary>
						</div>
					</div>
					<UpdateBanner />
				</div>
			) : (
				<NotAuthorized />
			)}
		</>
	);
};

export default RootLayout;
